/**
 * EvaluationShell.jsx — Phase 2C Assessment Evaluation Module Shell
 *
 * Left-nav workspace layout for the full Phase 2C evaluation workflow.
 * Role-sensitive nav: items are shown/hidden based on the actor's role.
 *
 * Role groups:
 *   Coordinator  → all items
 *   Faculty      → Marking (own assigned cases)
 *   Moderator    → Marking + Moderation
 *   Ratifier     → Ratification
 *   Norm. Auth.  → Normalization
 */
import React from "react";
import { NavLink } from "react-router-dom";
import {
  LayoutDashboard,
  UserPlus,
  ClipboardList,
  Scale,
  BarChart3,
  ShieldCheck,
  CheckCircle2,
  BookOpen,
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";

const ALL_NAV_ITEMS = [
  {
    to: "/assessment-evaluation",
    label: "Overview",
    icon: LayoutDashboard,
    exact: true,
    testid: "evalnav-overview",
    roles: null, // everyone
  },
  {
    to: "/assessment-evaluation/marker-allocation",
    label: "Marker Allocation",
    icon: UserPlus,
    exact: false,
    testid: "evalnav-allocation",
    roles: ["super_admin", "institution_admin", "programme_manager", "registrar", "exam_admin"],
  },
  {
    to: "/assessment-evaluation/marking",
    label: "Marking",
    icon: ClipboardList,
    exact: false,
    testid: "evalnav-marking",
    roles: null, // all authenticated (filtered to own cases on backend)
  },
  {
    to: "/assessment-evaluation/moderation",
    label: "Moderation",
    icon: Scale,
    exact: false,
    testid: "evalnav-moderation",
    roles: ["super_admin", "institution_admin", "programme_manager", "faculty", "instructor"],
  },
  {
    to: "/assessment-evaluation/normalization",
    label: "Normalization",
    icon: BarChart3,
    exact: false,
    testid: "evalnav-normalization",
    roles: ["super_admin", "institution_admin", "programme_manager"],
  },
  {
    to: "/assessment-evaluation/ratification",
    label: "Ratification",
    icon: CheckCircle2,
    exact: false,
    testid: "evalnav-ratification",
    roles: ["super_admin", "institution_admin", "dean", "programme_manager", "registrar", "exam_admin"],
  },
  {
    to: "/assessment-evaluation/gradebook",
    label: "Gradebook",
    icon: BookOpen,
    exact: false,
    testid: "evalnav-gradebook",
    roles: ["super_admin", "institution_admin", "programme_manager", "registrar", "exam_admin"],
  },
];

const STUDENT_ROLES = new Set(["student"]);

export function EvaluationShell({ children, title, breadcrumb }) {
  const { user } = useAuth();
  if (!user) return null;

  if (STUDENT_ROLES.has(user.role)) {
    return (
      <div
        className="min-h-screen bg-slate-950 flex items-center justify-center p-8"
        data-testid="eval-access-denied"
      >
        <div className="max-w-md w-full rounded-xl border border-red-800/50 bg-red-950/20 p-8 text-center">
          <div className="w-12 h-12 rounded-full bg-red-900/40 flex items-center justify-center mx-auto mb-4">
            <ShieldCheck className="w-6 h-6 text-red-400" />
          </div>
          <h2 className="text-xl font-semibold text-slate-100 mb-2">Access Denied</h2>
          <p className="text-sm text-slate-400 mb-4">
            The Assessment Evaluation module is not available to students.
          </p>
          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border border-red-700/60 bg-red-950/50 text-red-300">
            403 Permission Denied
          </span>
        </div>
      </div>
    );
  }

  const visibleItems = ALL_NAV_ITEMS.filter(
    (item) => item.roles === null || item.roles.includes(user.role)
  );

  return (
    <div className="min-h-screen bg-slate-950 flex" data-testid="evaluation-shell">
      {/* Left sub-nav */}
      <aside className="w-52 flex-shrink-0 border-r border-slate-800 bg-slate-900/60 flex flex-col pt-6 pb-8">
        <div className="px-4 mb-6">
          <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">
            Assessment Evaluation
          </span>
        </div>
        <nav className="flex flex-col gap-0.5 px-2">
          {visibleItems.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.exact}
                data-testid={item.testid}
                className={({ isActive }) =>
                  `flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-colors ${
                    isActive
                      ? "bg-indigo-900/40 text-indigo-300 font-medium"
                      : "text-slate-400 hover:text-slate-200 hover:bg-slate-800/60"
                  }`
                }
              >
                <Icon className="w-4 h-4 flex-shrink-0" />
                {item.label}
              </NavLink>
            );
          })}
        </nav>
      </aside>

      {/* Main content */}
      <main className="flex-1 overflow-auto">
        <div className="border-b border-slate-800 bg-slate-900/40 px-8 py-5">
          {breadcrumb && (
            <div className="text-xs text-slate-500 mb-1 flex items-center gap-1">
              <span>Academic Operations</span>
              <span className="mx-1">›</span>
              <span className="text-slate-400">Assessment Evaluation</span>
              {breadcrumb && (
                <>
                  <span className="mx-1">›</span>
                  <span className="text-indigo-400">{breadcrumb}</span>
                </>
              )}
            </div>
          )}
          {title && (
            <h1 className="text-xl font-semibold text-slate-100 tracking-tight">{title}</h1>
          )}
        </div>
        <div className="p-8">{children}</div>
      </main>
    </div>
  );
}
